class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.overlay = this.querySelector('.sg-drawer__overlay');
    this.contentWrapper = this.querySelector('.sg-drawer__contents');

    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.close = this.close.bind(this);

    this.tableWrapper = this.querySelector('.sg-table-wrapper');
    this.sizeGuideTable = this.tableWrapper?.querySelector('table') || null;
    /** @type {NodeListOf<Button>} */
    this.sizeGuideSwitches = this.querySelectorAll('.sg-measurements-switch button');
    /** @type {HTMLIFrameElement | null} */
    this.videoEmbed = this.querySelector('.embed-container iframe');

    this.setMeasureUnits(this.measureUnits);
    this.addEventListener('keyup', this.handleKeyUp);
    this.overlay.addEventListener('click', this.close);
    this.sizeGuideSwitches.forEach((button) => {
      button.addEventListener('click', (e) => {
        const unit = e.target.getAttribute('data-unit');
        this.setMeasureUnits(unit);
      })
    })
    this.createCellConversions();
  }
  disconnectedCallback() {
    this.removeEventListener('keyup', this.handleKeyUp);
    this.overlay.removeEventListener('click', this.close);
  }
  handleKeyUp(e) {
    if (e.code === 'Escape') this.close();
  }
  /**
   * Open the drawer.
   * @param {HTMLElement} triggeredBy - Element that triggered drawer to open.
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // mimic await for the animation to finish
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const focusTrapContainer = document.getElementById('SizeGuideDrawer');
      const focusTrapElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(focusTrapContainer, focusTrapElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }
  /** Close drawer. */
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (this.videoEmbed)
      this.stopEmbed();
  }
  /** Stop the youtube embed by replacing the src attribute. */
  stopEmbed() {
    const src = this.videoEmbed.src;
    this.videoEmbed.src = '';
    this.videoEmbed.src = src;
  }
  /**
   * Set active element that triggered drawer to open.
   * @param {HTMLElement} element - Element that triggered drawer to open.
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
  /** Get preferred unit of measurement from localstorage.
   * @returns {'in' | 'cm'} Unit of measurement
  */
  get measureUnits() {
    return localStorage.getItem('sizeGuideUnits') || 'in';
  }
  /**
   * Set unit of measurement units for size guide table.
   * @param {'in' | 'cm'} unit - Unit of measurement
   */
  setMeasureUnits(unit) {
    localStorage.setItem('sizeGuideUnits', unit);
    this.contentWrapper.setAttribute('data-units', unit);
  }
  /**
   * Remove quotes from string.
   * @param {string} str - String to remove quotes from.
   * @returns {string} String without quotes.
   */
  removeQuotes (str) {
    return str.replace(/['"”]+/g, '');
  }

  /**
   * Check if unit is in inches.
   * @param {string} unit - Unit to check.
   */
  isInch (unit) {
    const regex = /^\d+(\.\d+)?(?!x)$/;
    const trimmed = this.removeQuotes(unit);
    return regex.test(trimmed);
  }
  /**
   * Convert fraction to decimal.
   * @param {string} fracString - Fraction string to convert to decimal.
   * @returns {number} Decimal value of fraction.
   */
  fracToDec (fracString) {
    const [whole, fraction] = fracString.split(' ');
    const decimal = parseFloat(whole);
    if (!fraction) return decimal.toFixed(2);

    const [numerator, denominator] = fraction.split('/');
    return (decimal + parseFloat(numerator) / parseFloat(denominator)).toFixed(2);
  }

  /**
   * Convert inches to centimeters.
   * @param {string} inches - Inches value to convert.
   * @returns {string} Centimeters value.
   */
  inToCm (inches) {
    return (parseFloat(inches) * 2.54).toFixed(1)
  };
  /** Convert cell values in the size guide table from inches to centimeters. */
  createCellConversions() {
    const table = this.sizeGuideTable;
    if (!table) return;

    table.querySelectorAll('tr').forEach((row) => {
      row.querySelectorAll('td').forEach((cell, _i) => {
        const cellTextContent = cell.textContent.trim();
        let cellValue = this.removeQuotes(cellTextContent);
        let convertedValue = cellValue;

        const cellHasNumber = !!cellValue.match(/\d+/);
        if (!cellHasNumber) return;

        const cellIsRange = !!cellValue.match(/-/);
        const cellIsFraction = !!cellValue.match(/\d+\s\d+\/\d+/);
        const cellIsInch = this.isInch(cellValue);

        if (cellIsRange) {
          const range = cellValue.split('-').map(val => val.trim());
          const cm1 = Math.round(this.inToCm(this.fracToDec(range[0])) * 2) / 2;
          const cm2 = Math.round(this.inToCm(this.fracToDec(range[1])) * 2) / 2;
          convertedValue = `${cm1}-${cm2}`;
        } else if (cellIsFraction) {
          convertedValue = Math.round(this.inToCm(this.fracToDec(cellValue)) * 2) / 2;
        } else if (cellIsInch) {
          convertedValue = Math.round(this.inToCm(cellValue) * 2) / 2;
        } else {
          return;
        }

        // console.log(cellTextContent, cellValue, convertedValue);

        cell.innerHTML = `
          <span data-unit="in">${cellTextContent}</span>
          <span data-unit="cm">${convertedValue}</span>
        `
      })
    })
  }
}

customElements.define('size-guide-drawer', SizeGuideDrawer);
